// API service for communicating with Oma backend

import type { ApiResponse, ChatResponse, InfoResponse } from '../types';
import { OPENING_QUESTIONS } from '../data/opening-questions';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Add CSRF protection headers
      const baseHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      
      // Merge with any additional headers from options
      const optionHeaders = options.headers as Record<string, string> | undefined;
      const headers: Record<string, string> = {
        ...baseHeaders,
        ...(optionHeaders || {}),
      };

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async sendMessage(
    message: string,
    conversationId?: string
  ): Promise<ApiResponse<ChatResponse>> {
    return this.request<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversationId,
      }),
    });
  }

  async getInfo(): Promise<ApiResponse<InfoResponse>> {
    return this.request<InfoResponse>('/info');
  }

  async getOpeningQuestion(): Promise<ApiResponse<{ question: string; id: string }>> {
    // Get random question from local data
    const index = Math.floor(Math.random() * OPENING_QUESTIONS.length);
    const question = OPENING_QUESTIONS[index];
    
    return {
      success: true,
      data: {
        question: question.question,
        id: question.id
      }
    };
  }
}

export const api = new ApiService();
